import type { ParsedQs } from 'qs'
import type { UiState } from 'instantsearch.js'
import type { Hit } from 'instantsearch.js/es/types'
import { discussionsAlgoliaIndex, SORTING_OPTIONS } from './configs'

type SortToken = 'recent' | 'newest' | 'oldest' | 'popularity'
type ParsedQsValue = ParsedQs[keyof ParsedQs]

const tokenToIndex = new Map<SortToken, string>()
const indexToToken = new Map<string, SortToken>()
const DEFAULT_TOKEN: SortToken = 'newest'
const DEFAULT_INDEX = discussionsAlgoliaIndex

for (const opt of SORTING_OPTIONS) {
  tokenToIndex.set(opt.type as SortToken, opt.value)
  indexToToken.set(opt.value, opt.type as SortToken)
}

function tokenFromIndexName(indexName?: string): SortToken {
  if (!indexName) {
    return DEFAULT_TOKEN
  }
  return indexToToken.get(indexName) ?? DEFAULT_TOKEN
}

function indexNameFromToken(raw: string | null): string {
  const token = (raw as SortToken) || DEFAULT_TOKEN
  return tokenToIndex.get(token) ?? DEFAULT_INDEX
}

export interface QueryToSearchState {
  uiState: UiState
  other?: {
    filter?: ParsedQs[keyof ParsedQs]
    aiContentType?: ParsedQs[keyof ParsedQs]
  }
}

export function searchStateToQuery({ uiState }: QueryToSearchState): string {
  const indexState = uiState[discussionsAlgoliaIndex] ?? {}
  const params = new URLSearchParams()

  if (indexState.query) {
    params.set('q', String(indexState.query))
  }
  if (typeof indexState.page === 'number') {
    params.set('page', String(indexState.page))
  }

  const token = tokenFromIndexName(
    typeof indexState.sortBy === 'string' ? indexState.sortBy : DEFAULT_INDEX,
  )

  if (token !== DEFAULT_TOKEN) {
    params.set('sortBy', token)
  }

  return params.toString()
}

export function getSearchState({ url }: { url: string }): { uiState: UiState } {
  const sp = new URLSearchParams(url)

  const query = sp.get('q') ?? ''
  const pageRaw = sp.get('page')
  const page = Number.isFinite(Number(pageRaw)) ? Number(pageRaw) : 0

  const sortBy = indexNameFromToken(sp.get('sortBy'))

  return {
    uiState: {
      [discussionsAlgoliaIndex]: { query, page, sortBy },
    },
  }
}
export interface DiscussionPostObject extends Hit {
  objectID: string
  topic_id: string
  headline: string
  content: string
  create_time: Date
  clubhouse_name: string
  topic_slug: string
}

export const queryToSearchState = (queryObject: ParsedQs): QueryToSearchState => {
  const { query = '', page, filter, topic_slug } = queryObject

  const state: UiState = {
    [discussionsAlgoliaIndex]: {
      query: decodeURIComponent(String(query)),
      // oxlint-disable-next-line no-negated-condition
      page: !Number(page) ? 1 : Number(page),
      menu: {
        topic_slug: topic_slug ? decodeURIComponent(String(topic_slug)) : '',
      },
    },
  }

  return {
    uiState: state,
    other: {
      filter: decodeQueryArray(filter),
    },
  }
}

export const decodeQueryArray = (value: ParsedQsValue): string[] => {
  if (typeof value === 'string') {
    if (value.length === 0) {
      return []
    }
    // oxlint-disable-next-line unicorn/prefer-includes
    if (value.indexOf(',') > -1) {
      return value.split(',').map(decodeURIComponent)
    }
    return [decodeURIComponent(value)]
  }

  if (value && Array.isArray(value)) {
    const stringValues: string[] = []
    value.forEach((item: ParsedQsValue) => {
      if (typeof item === 'string') {
        stringValues.push(item)
      }
    })
    return stringValues.map(decodeURIComponent)
  }

  return []
}

export function isSearchPage(pathname: string) {
  return pathname.includes('/search/')
}
